import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Surface, Badge, Button, Grid, PageHeader, StatCard as DSStatCard } from '../../ui/components';
import { FileText, DollarSign, TrendingUp, Users, Landmark, Gavel, CheckCircle, Clock, AlertTriangle, RefreshCw, Mail, UserX } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const ICON_MAP = { 'FileText': FileText, 'DollarSign': DollarSign, 'TrendingUp': TrendingUp, 'Users': Users, 'Landmark': Landmark, 'Gavel': Gavel };

const AdminStatCard = ({ title, value, subtitle, Icon, color }) => (
  <Surface padding="var(--lk-space-5)">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div className="lk-overline" style={{ marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-text-primary)', letterSpacing: '-0.02em' }}>{value}</div>
        {subtitle && <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-lg)', backgroundColor: color || 'var(--lk-gray-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {Icon && <Icon size={22} color="var(--lk-text-secondary)" />}
      </div>
    </div>
  </Surface>
);

const AdminOverview = () => {
  const [stats, setStats] = useState({
    totalApplications: 0,
    activeUnderwriters: 0,
    totalUnderwriters: 0,
    activeLenders: 0,
    totalBids: 0,
    totalLoanVolume: 0,
    avgReadiness: 0,
    completeApplications: 0,
    incompleteApplications: 0,
    abandonedApplications: 0,
    applicationsToday: 0,
    applicationsThisWeek: 0,
    applicationsThisMonth: 0,
    conversionRate: 0,
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [recentBids, setRecentBids] = useState([]);
  const [onlineUnderwriters, setOnlineUnderwriters] = useState([]);
  const [stalledApplications, setStalledApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch applications
      const { data: applications } = await supabase
        .from('loan_applications')
        .select('*')
        .order('created_at', { ascending: false });

      // Fetch bids
      const { data: bids } = await supabase
        .from('lender_bids')
        .select('*')
        .order('created_at', { ascending: false });

      // Fetch underwriter profiles
      const { data: underwriters } = await supabase
        .from('underwriter_profiles')
        .select('*');

      // Fetch lender profiles
      const { data: lenders } = await supabase
        .from('lender_profiles')
        .select('*');

      // Calculate stats
      const totalLoanVolume = applications?.reduce((sum, app) => sum + (parseFloat(app.loan_amount) || 0), 0) || 0;
      const avgReadiness = applications?.length > 0
        ? applications.reduce((sum, app) => sum + (app.readiness_score || 0), 0) / applications.length
        : 0;

      const completeStatuses = ['submitted_for_underwriting', 'cleared_for_marketplace', 'in_marketplace', 'bid_accepted', 'closed'];
      const incompleteStatuses = ['draft', 'in_progress'];
      const completeApplications = applications?.filter(app => completeStatuses.includes(app.status))?.length || 0;
      const incompleteApplications = applications?.filter(app => incompleteStatuses.includes(app.status))?.length || 0;
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const abandonedApplications = applications?.filter(app => 
        incompleteStatuses.includes(app.status) && 
        new Date(app.updated_at) < sevenDaysAgo
      )?.length || 0;

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - 7);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const applicationsToday = applications?.filter(app => new Date(app.created_at) >= todayStart)?.length || 0;
      const applicationsThisWeek = applications?.filter(app => new Date(app.created_at) >= weekStart)?.length || 0;
      const applicationsThisMonth = applications?.filter(app => new Date(app.created_at) >= monthStart)?.length || 0;

      const conversionRate = applications?.length > 0 
        ? (completeApplications / applications.length) * 100 
        : 0;

      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      const stalled = applications?.filter(app => 
        incompleteStatuses.includes(app.status) && 
        new Date(app.updated_at) < threeDaysAgo &&
        app.borrower_info?.email
      )?.slice(0, 10) || [];

      setStats({
        totalApplications: applications?.length || 0,
        activeUnderwriters: underwriters?.filter(u => u.is_available)?.length || 0,
        totalUnderwriters: underwriters?.length || 0,
        activeLenders: lenders?.filter(l => l.is_active)?.length || 0,
        totalBids: bids?.length || 0,
        totalLoanVolume,
        avgReadiness,
        completeApplications,
        incompleteApplications,
        abandonedApplications,
        applicationsToday,
        applicationsThisWeek,
        applicationsThisMonth,
        conversionRate,
      });

      setRecentApplications(applications?.slice(0, 10) || []);
      setRecentBids(bids?.slice(0, 10) || []);
      setOnlineUnderwriters(underwriters?.filter(u => u.is_available) || []);
      setStalledApplications(stalled);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'secondary',
      in_progress: 'info',
      submitted_for_underwriting: 'warning',
      cleared_for_marketplace: 'success',
      in_marketplace: 'primary',
      bid_accepted: 'success',
      closed: 'success',
      needs_work: 'danger',
    };
    return colors[status] || 'secondary';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getDaysSince = (dateStr) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div style={{ padding: 'var(--lk-space-8)' }}>
        <div className="lk-spinner lk-spinner-lg" style={{ margin: '20vh auto' }} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Dashboard Overview"
        actions={
          <Button variant="secondary" icon={RefreshCw} onClick={fetchDashboardData} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {/* Primary Stats */}
      <Grid cols={6} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <AdminStatCard title="Total Applications" value={stats.totalApplications} subtitle="All time" Icon={FileText} color="var(--lk-primary-50)" />
        <AdminStatCard title="Total Loan Volume" value={formatCurrency(stats.totalLoanVolume)} subtitle="Across all applications" Icon={DollarSign} color="var(--lk-success-50)" />
        <AdminStatCard title="Avg Readiness" value={`${stats.avgReadiness.toFixed(0)}/100`} subtitle="Average score" Icon={TrendingUp} color="var(--lk-primary-50)" />
        <AdminStatCard title="Underwriters Online" value={`${stats.activeUnderwriters}/${stats.totalUnderwriters}`} subtitle="Available now" Icon={Users} color="var(--lk-warning-50)" />
        <AdminStatCard title="Active Lenders" value={stats.activeLenders} subtitle="In network" Icon={Landmark} color="var(--lk-success-50)" />
        <AdminStatCard title="Total Bids" value={stats.totalBids} subtitle="Lender bids submitted" Icon={Gavel} color="var(--lk-danger-50)" />
      </Grid>

      {/* Completion Status & Conversion */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--lk-space-4)', marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-6)">
          <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 600, marginBottom: 'var(--lk-space-4)' }}>Application Completion Status</div>
          <Grid cols={3} gap="var(--lk-space-4)" style={{ textAlign: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <CheckCircle size={22} color="var(--lk-success-500)" />
                <span style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>{stats.completeApplications}</span>
              </div>
              <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>Complete</div>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>
                {stats.totalApplications > 0 ? ((stats.completeApplications / stats.totalApplications) * 100).toFixed(1) : 0}%
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <Clock size={22} color="var(--lk-warning-500)" />
                <span style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-warning-600)' }}>{stats.incompleteApplications}</span>
              </div>
              <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>In Progress</div>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>
                {stats.totalApplications > 0 ? ((stats.incompleteApplications / stats.totalApplications) * 100).toFixed(1) : 0}%
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={22} color="var(--lk-danger-500)" />
                <span style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-danger-600)' }}>{stats.abandonedApplications}</span>
              </div>
              <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>Abandoned (7+ days)</div>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Need re-engagement</div>
            </div>
          </Grid>
          {/* Stacked progress bar */}
          <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginTop: 'var(--lk-space-4)', backgroundColor: 'var(--lk-gray-100)' }}>
            <div style={{ width: `${stats.totalApplications > 0 ? (stats.completeApplications / stats.totalApplications) * 100 : 0}%`, backgroundColor: 'var(--lk-success-500)', transition: 'width 0.6s ease' }} />
            <div style={{ width: `${stats.totalApplications > 0 ? ((stats.incompleteApplications - stats.abandonedApplications) / stats.totalApplications) * 100 : 0}%`, backgroundColor: 'var(--lk-warning-400)', transition: 'width 0.6s ease' }} />
            <div style={{ width: `${stats.totalApplications > 0 ? (stats.abandonedApplications / stats.totalApplications) * 100 : 0}%`, backgroundColor: 'var(--lk-danger-400)', transition: 'width 0.6s ease' }} />
          </div>
        </Surface>
        <Surface padding="var(--lk-space-6)" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Conversion Rate</div>
          <div style={{ fontSize: '3.5rem', fontWeight: 700, letterSpacing: '-0.03em', color: stats.conversionRate >= 50 ? 'var(--lk-success-600)' : stats.conversionRate >= 25 ? 'var(--lk-warning-600)' : 'var(--lk-danger-600)' }}>
            {stats.conversionRate.toFixed(1)}%
          </div>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>Applications completed</div>
        </Surface>
      </div>

      {/* Activity Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--lk-space-4)', marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-6)">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lk-space-3)' }}>
            <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 600 }}>Applications Over Time</div>
            <div style={{ display: 'flex', gap: 'var(--lk-space-2)' }}>
              <Badge variant="primary">Today: {stats.applicationsToday}</Badge>
              <Badge variant="neutral">Week: {stats.applicationsThisWeek}</Badge>
              <Badge variant="neutral">Month: {stats.applicationsThisMonth}</Badge>
            </div>
          </div>
          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)' }}>Last 30 days activity</div>
        </Surface>
        <Surface padding="var(--lk-space-6)">
          <div style={{ fontSize: 'var(--lk-text-lg)', fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Online Underwriters</div>
          {onlineUnderwriters.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              {onlineUnderwriters.slice(0, 5).map((uw) => (
                <div key={uw.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'var(--lk-warning-100)', color: 'var(--lk-warning-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600 }}>
                    {uw.first_name?.charAt(0)}{uw.last_name?.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 'var(--lk-text-sm)' }}>{uw.first_name} {uw.last_name}</div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--lk-text-quaternary)' }}>NMLS #{uw.nmls_number || 'N/A'}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--lk-success-500)' }} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--lk-space-6) 0' }}>
              <UserX size={36} color="var(--lk-gray-300)" />
              <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-quaternary)', marginTop: 'var(--lk-space-2)' }}>No underwriters online</div>
            </div>
          )}
        </Surface>
      </div>

      {/* Stalled Applications */}
      {stalledApplications.length > 0 && (
        <Surface padding="0" style={{ marginBottom: 'var(--lk-space-6)', border: '1px solid var(--lk-danger-200)' }}>
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-6)', backgroundColor: 'var(--lk-danger-50)', display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <AlertTriangle size={18} color="var(--lk-danger-600)" />
            <span style={{ fontWeight: 600, color: 'var(--lk-danger-700)' }}>Re-engagement Opportunities ({stalledApplications.length})</span>
            <span style={{ marginLeft: 'auto', fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Applications stalled 3+ days with contact info</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Application ID</th>
                  <th>Borrower</th>
                  <th>Email</th>
                  <th>Amount</th>
                  <th>Score</th>
                  <th>Days Stalled</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stalledApplications.map((app) => (
                  <tr key={app.id}>
                    <td style={{ fontWeight: 500 }}>{app.anonymous_id || app.id.slice(0, 8)}</td>
                    <td>{app.borrower_info?.first_name} {app.borrower_info?.last_name}</td>
                    <td style={{ color: 'var(--lk-primary-600)' }}>{app.borrower_info?.email}</td>
                    <td>{formatCurrency(app.loan_amount || 0)}</td>
                    <td>
                      <Badge variant={app.readiness_score >= 80 ? 'success' : app.readiness_score >= 60 ? 'warning' : 'danger'}>
                        {app.readiness_score || 0}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="danger">{getDaysSince(app.updated_at)} days</Badge>
                    </td>
                    <td>
                      <Button size="sm" variant="ghost" icon={Mail} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
      )}

      {/* Recent Activity Tables */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--lk-space-4)' }}>
        <Surface padding="0" style={{ overflow: 'hidden' }}>
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--lk-border)' }}>
            <span style={{ fontWeight: 600 }}>Recent Applications</span>
            <Button size="sm" variant="ghost" onClick={() => window.location.href = '/admin/applications'}>View All</Button>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 400 }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Borrower</th>
                  <th>Amount</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentApplications.length > 0 ? (
                  recentApplications.map((app) => (
                    <tr key={app.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {app.anonymous_id || `LK-${app.id.slice(0, 8).toUpperCase()}`}
                        {app.borrower_info?.is_test && (
                          <Badge variant="warning" style={{ marginLeft: 4, fontSize: '0.6rem' }}>T</Badge>
                        )}
                      </td>
                      <td>{app.borrower_info?.first_name || '-'} {app.borrower_info?.last_name || ''}</td>
                      <td style={{ fontWeight: 500 }}>{formatCurrency(app.loan_amount || 0)}</td>
                      <td>
                        <Badge variant={app.readiness_score >= 80 ? 'success' : app.readiness_score >= 60 ? 'warning' : 'danger'}>
                          {app.readiness_score || 0}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={getStatusColor(app.status)}>
                          {app.status?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{formatDate(app.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-6)' }}>No applications yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Surface>

        <Surface padding="0" style={{ overflow: 'hidden' }}>
          <div style={{ padding: 'var(--lk-space-4) var(--lk-space-6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--lk-border)' }}>
            <span style={{ fontWeight: 600 }}>Recent Lender Bids</span>
            <Button size="sm" variant="ghost">View All</Button>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 400 }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Lender</th>
                  <th>Rate</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {recentBids.length > 0 ? (
                  recentBids.map((bid) => (
                    <tr key={bid.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {bid.application_id?.slice(0, 8).toUpperCase()}...
                      </td>
                      <td>{bid.lender_name || bid.lender_id?.slice(0, 8) || '-'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--lk-primary-600)' }}>{Number(bid.interest_rate).toFixed(3)}%</td>
                      <td>
                        <Badge variant={bid.status === 'accepted' ? 'success' : bid.status === 'rejected' ? 'danger' : 'neutral'}>
                          {bid.status}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{formatDate(bid.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-6)' }}>No bids yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Surface>
      </div>

      {/* Floating AI Assistant & Knowledge */}
      <FloatingOwenAssistant 
        context="admin-overview"
        contextData={{ stats }}
      />
      <FloatingKnowledge 
        customContent={{
          label: 'Admin',
          title: 'Platform administration',
          body: 'Monitor system health and manage all platform operations.',
          bullets: [
            'Track key metrics and performance indicators',
            'Monitor user activity and application flow',
            'Manage underwriter and lender accounts',
            'Review system alerts and issues',
          ],
          resources: [
            { label: 'Admin guide', href: '#' },
            { label: 'System documentation', href: '#' },
          ],
        }}
      />
    </div>
  );
};

export default AdminOverview;
